import {
    Euros,
    Hours,
    max,
    min,
    multiplyWithFactor,
    multiplyWithPercent,
    multiplyWithRate,
    Percent,
    Rate,
    round,
    subtract,
} from "@pentacode/openapi";
import { dateAdd, parseTime } from "../utils";
import { getAncillaryCostFactor, getHourlyRate } from "../payroll";
import { Bonus, BonusCompType, BonusLegalType, BonusType, BonusTypeMode, Company, Employee, TimeEntry } from "../model";
import { Interval, overlap, subtractAllIntervals, union } from "./interval";
import { hour } from "./consts";

export type BonusResult = {
    bonusId: number;
    type: {
        id: number;
        name: string;
        legalType?: BonusLegalType;
    };
    duration: Hours;
    percent?: Percent;
    hourlyRate?: Rate<Euros, Hours>;
    taxFree: boolean;
    wages: Euros;
    ancillaryCosts: Euros;
};

export function getBonusResults(company: Company, employee: Employee, entry: TimeEntry, work: Interval): BonusResult[] {
    const contract = employee.getContractForDate(entry.date);

    if (!contract) {
        return [];
    }

    const bonuses: {
        bonus: Bonus;
        type: BonusType;
        intervals: Interval[];
    }[] = [];

    for (const bonus of contract.bonuses) {
        const type = company.bonusTypes?.find((t) => t.id === bonus.typeId);

        if (!type || (bonus.positionId && entry.positionId !== bonus.positionId)) {
            continue;
        }

        const mustStartBefore = type.shiftMustStartBefore && parseTime(entry.date, type.shiftMustStartBefore);
        if (mustStartBefore && entry.start >= mustStartBefore) {
            continue;
        }

        const matchingDates =
            type.mode === BonusTypeMode.Daily
                ? [entry.date]
                : [entry.date, dateAdd(entry.date, { days: 1 })].filter((date) =>
                      type.matchesDate(date, company.country)
                  );

        let intervals: Interval[] = [];
        for (const date of matchingDates) {
            intervals.push(
                ...type.intervals.map(([start, end]) => [parseTime(date, start), parseTime(date, end)] as Interval)
            );
        }

        if (!intervals.length) {
            continue;
        }

        intervals = union(...intervals);

        bonuses.push({
            bonus,
            type,
            intervals,
        });
    }

    for (const bonus of bonuses) {
        for (const override of bonus.type.overrides) {
            const otherBonuses = bonuses.filter((b) => b.type.id === override.objectId);
            for (const otherBonus of otherBonuses) {
                otherBonus.intervals = subtractAllIntervals(otherBonus.intervals, bonus.intervals);
            }
        }
    }

    const workDuration = ((work[1].getTime() - work[0].getTime()) / hour) as Hours;

    const results: BonusResult[] = [];

    for (const { bonus, type, intervals } of bonuses) {
        let duration = overlap(work, intervals);

        if (
            (type.minDuration && duration < type.minDuration) ||
            (type.minDurationPercent && (duration / workDuration) * 100 < type.minDurationPercent)
        ) {
            duration = 0 as Hours;
        }

        let hourlyRate: Rate<Euros, Hours> | undefined;

        switch (type.compType) {
            case BonusCompType.FixedAmount:
                results.push({
                    bonusId: bonus.id,
                    type: {
                        id: type.id,
                        name: type.name,
                        legalType: type.legalType,
                    },
                    taxFree: type.taxFree,
                    ancillaryCosts: 0 as Euros,
                    duration,
                    wages: bonus.fixedAmount || type.fixedAmountDefault || (0 as Euros),
                });
                continue;
            case BonusCompType.FixedHourlyRate:
                hourlyRate = bonus.hourlyRate || type.fixedHourlyRateDefault || (0 as Rate<Euros, Hours>);
                results.push({
                    bonusId: bonus.id,
                    type: {
                        id: type.id,
                        name: type.name,
                        legalType: type.legalType,
                    },
                    taxFree: type.taxFree,
                    ancillaryCosts: 0 as Euros,
                    duration,
                    hourlyRate,
                    wages: multiplyWithRate(round(duration, 2), hourlyRate),
                });
                continue;
            case BonusCompType.ModifiedHourlyRate: {
                hourlyRate = getHourlyRate(company, contract, entry.positionId);
                const percent = bonus.percent || type.defaultPercent || (0 as Percent);
                const taxFreeUpToRate = type.taxFree ? type.taxFreeUpToRate : (0 as Rate<Euros, Hours>);
                const taxFreeRate = hourlyRate && min(hourlyRate, taxFreeUpToRate);
                const taxedRate = hourlyRate && max(0 as Rate<Euros, Hours>, subtract(hourlyRate, taxFreeUpToRate));

                if (taxFreeRate || (!hourlyRate && !!taxFreeUpToRate)) {
                    const wages = multiplyWithPercent(multiplyWithRate(round(duration, 2), taxFreeRate), percent);
                    results.push({
                        bonusId: bonus.id,
                        type: {
                            id: type.id,
                            name: type.name,
                            legalType: type.legalType,
                        },
                        taxFree: true,
                        hourlyRate: taxFreeRate,
                        ancillaryCosts: 0 as Euros,
                        wages,
                        duration,
                        percent,
                    });
                }

                if (taxedRate || (!hourlyRate && !taxFreeUpToRate)) {
                    const wages = multiplyWithPercent(multiplyWithRate(round(duration, 2), taxedRate), percent);
                    const ancillaryCosts = multiplyWithFactor(
                        wages,
                        getAncillaryCostFactor(contract.employmentType)[0]
                    );

                    results.push({
                        bonusId: bonus.id,
                        type: {
                            id: type.id,
                            name: type.name,
                            legalType: type.legalType,
                        },
                        taxFree: false,
                        hourlyRate: taxedRate,
                        ancillaryCosts,
                        wages,
                        duration,
                        percent,
                    });
                }
                break;
            }
        }
    }

    return results;
}
